import { useState } from "react";
import { Link } from "react-router-dom";
import { requestUsernameReminder } from "../../services/authService";
import { normalizeEmail, validateEmail } from "../../utils/emailValidation";
import { BrandField } from "../form/BrandField";
import { BrandButton } from "../ui/BrandButton";
import { FormCard } from "../form/FormCard";
import { brandFormCardClass } from "../form/fieldStyles";

const initialValues = {
  email: "",
};

export function ForgotUsernameForm({ logoSrc }) {
  const [values, setValues] = useState(initialValues);
  const [touched, setTouched] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const showError = (field) => touched[field] && fieldErrors[field];

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((current) => ({
      ...current,
      [name]: name === "email" ? value.toLowerCase() : value,
    }));
    setSubmitError("");
    setSuccessMessage("");
    setFieldErrors((current) => {
      if (!current[name]) return current;
      const next = { ...current };
      delete next[name];
      return next;
    });
  };

  const handleBlur = (event) => {
    const { name } = event.target;
    setTouched((current) => ({ ...current, [name]: true }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const emailError = validateEmail(values.email);
    setTouched({ email: true });

    if (emailError) {
      setFieldErrors({ email: emailError });
      return;
    }

    const normalized = {
      email: normalizeEmail(values.email),
    };

    setIsSubmitting(true);
    setSubmitError("");
    setFieldErrors({});

    try {
      const response = await requestUsernameReminder(normalized);
      setSuccessMessage(response.message);
      setValues(initialValues);
      setTouched({});
    } catch (error) {
      if (error.code === "TOO_MANY_REQUESTS") {
        setSubmitError(error.message);
        return;
      }

      if (error.code === "MAIL_DELIVERY_FAILED") {
        setSubmitError(error.message);
        return;
      }

      if (Array.isArray(error.details)) {
        const mappedErrors = error.details.reduce((acc, item) => {
          if (item?.path) {
            acc[item.path] = item.message;
          }
          return acc;
        }, {});

        if (Object.keys(mappedErrors).length > 0) {
          setFieldErrors(mappedErrors);
          return;
        }
      }

      setSubmitError(error.message || "No pudimos procesar la solicitud.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormCard className={brandFormCardClass}>
      <div className="mb-6 flex flex-col items-center text-center">
        <h2 className="mt-5 font-display text-3xl text-brand-primary sm:text-4xl">
          Recordar nombre de usuario
        </h2>
        <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600 sm:text-base">
          Ingresá el email asociado a tu cuenta y te enviaremos todos los
          nombres de usuario registrados allí.
        </p>
      </div>

      {submitError ? (
        <div className="mb-5 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {submitError}
        </div>
      ) : null}

      {successMessage ? (
        <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {successMessage}
        </div>
      ) : null}

      <form className="grid gap-4" onSubmit={handleSubmit} noValidate>
        <BrandField
          label="Email"
          name="email"
          value={values.email}
          onChange={handleChange}
          onBlur={handleBlur}
          error={showError("email") || fieldErrors.email}
          autoComplete="email"
          required
          showLabel={false}
          placeholder="Email"
        />

        <BrandButton
          type="submit"
          disabled={isSubmitting}
          variant="primary"
          radius="2xl"
          size="lg"
          className="w-full"
        >
          {isSubmitting ? "Enviando..." : "Enviar nombres de usuario"}
        </BrandButton>
      </form>
    </FormCard>
  );
}
